










































































import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';

import {
	Org,
	Product,
	SimproApi,
	SimproJob,
	SimproJobDetails,
	SimproJobWorkNote,
	SimproStaffDetails,
} from '@/api';
import { ApiUtils } from '@/common/ApiUtils';

import Toast from './toast.vue';
import InlineEdit from '@/components/InlineEdit.vue';
import SimproJobSetStatusModal from '@/components/display/SimproJobSetStatusModal.vue';
import EditJobModalsContainer from '@/components/add business objects/edit-job/EditJobModalsContainer.vue';

@Component({
	components: {
		EditJobModalsContainer,
		SimproJobSetStatusModal,
		InlineEdit,
	},
})
export default class SimproJobModal extends Vue {
	@Prop({ required: false, type: Object })
	public product!: Product | null;

	@Prop({ required: false, type: Object })
	public job!: (SimproJob & { simproJobId: number }) | null;

	@Prop({ required: false, type: Object })
	public jobDetails!: SimproJobDetails | null;

	@Prop(Array)
	public partnerOrgs?: Org[];

	public loadingJob: boolean = false;
	public workNotes: SimproJobWorkNote[] = [];
	public loadingWorkNotes: boolean = false;
	public jobStory: string | null = null;

	public get jobComplete(): boolean {
		return this.jobDetails?.stage === 'Complete';
	}

	public get jobOnHold(): boolean {
		return this.jobDetails?.status === 'Job : On Hold';
	}

	private simproApi: SimproApi = new SimproApi();
	private toast: Toast = new Toast();
	private simProUrl: string = process.env.VUE_APP_SIMPRO_BASE_URL + '/staff/editCostCentre.php';

	@Watch('job')
	public async onJobChanged(): Promise<void> {
		this.workNotes = [];
		if (!this.job?.id) {
			return;
		}
		this.loadingWorkNotes = true;
		await this.loadWorkNotes(this.job.id);
	}

	@Watch('jobDetails')
	public onJobDetailsChanged(): void {
		this.jobStory = this.jobDetails?.description ?? 'No description';
	}

	public userInitials(user: SimproStaffDetails): string {
		const nameParts: string[] = (user.Name ?? '').split(' ');
		return nameParts.map((part: string) => part[0].toUpperCase()).join('');
	}

	public dismiss(): void {
		this.$bvModal.hide('simproJobModal');
	}

	public openJobInSimPRO(): void {
		if (!this.job?.simproJobId) {
			return;
		}
		window.open(`${this.simProUrl}?jobID=${this.job.simproJobId}`, '_blank');
	}

	public async onJobStorySaved(): Promise<void> {
		if (!this.job?.id) {
			return;
		}
		try {
			await this.simproApi.updateSimproJobStory(this.job.id, { storyText: this.jobStory! });
			this.jobDetails!.description! = this.jobStory!;
			this.toast.makeToast('Job story updated successfully', 'Success', 'success');
		} catch (e) {
			this.resetJobStory();
			const errorResponse = ApiUtils.handleApiError(
				e,
				'Failed to update job story',
				`updating simPRO job story with ID '${this.job.id}'`,
			);
			this.toast.makeToast(errorResponse.errorMessage, 'Job Story Update Failed', 'danger', true);
		}
	}

	public onJobStoryChanged(newValue: string): void {
		this.jobStory = newValue;
	}

	public resetJobStory(): void {
		this.onJobDetailsChanged();
	}

	public async escalatePriority(): Promise<void> {
		if (!this.job?.id) {
			return;
		}
		try {
			await this.simproApi.escalateJobToPriority(this.job.id);
			this.toast.makeToast('Job story updated successfully', 'Success', 'success');
		} catch (e) {
			const errorResponse = ApiUtils.handleApiError(
				e,
				'Failed to escalate priority',
				`escalating simPRO job priority with ID '${this.job.id}'`,
			);
			this.toast.makeToast(errorResponse.errorMessage, 'Priority Escalation Error', 'danger', true);
		}
	}

	public async setStatusToNewService() {
		if (!this.job?.id) {
			return;
		}
		this.$bvModal.show('simproJobSetStatusModal');
	}

	private async loadWorkNotes(jobId: number): Promise<void> {
		try {
			const workNotesResponse = await this.simproApi.retrieveSimproWorkNotes(jobId);
			if (workNotesResponse.status === 200 && workNotesResponse.data) {
				this.workNotes = workNotesResponse.data;
			}
		} catch (e) {
			const errorResponse = ApiUtils.handleApiError(
				e,
				'Failed to load SimPRO job work notes',
				`getting simPRO job work notes with ID '${this.job.id}'`,
			);
			this.toast.makeToast(errorResponse.errorMessage, 'Error Loading Job Notes', 'danger', true);
		} finally {
			this.loadingWorkNotes = false;
		}
	}

	public openEditJobModal() {
		if (!this.job?.id) return;

		let modalId = '';
		switch (this.job.jobType) {
			case 'install':
				modalId = 'editInstallJobModal';
				break;
			case 'repair':
				modalId = 'editRepairJobModal';
				break;
			case 'remove':
				modalId = 'editRemoveJobModal';
				break;
		}
		this.$bvModal.show(modalId);
	}

	public async jobStoryUpdated(): Promise<void> {
		if (this.job?.id == null) return;
		this.loadingJob = true;
		try {
			const job = await this.simproApi.getSimproJobDetails(this.job?.id);
			this.jobStory = job.data?.data?.description ?? this.jobStory;
		} catch (e) {
			this.toast.makeToast('Failed to load new job story', 'Error', 'danger');
		}
		this.loadingJob = false;
	}
}
